import { createSelector } from 'reselect';
import type { AccessControlReducer, AccessResource, AccessResourcePermissions } from '../types/accessControlTypes';
import '../types/accessControlTypes';
import type { RootState } from 'client/js/redux';
import { ACCESS_FEATURE } from '../../../../shared/constants';
import type { AccessRoleResource, PermissionLevel } from '../../../../shared/types';
import {
  AD_NETWORKS,
  AGENCY_TAGGED_DATA,
  APP_LEVEL_SETTINGS,
  CHANNEL_LEVEL_SETTINGS,
  LINK_LEVEL_SETTINGS,
  READ_PERMISSION,
  REVENUE_DATA,
  WRITE_PERMISSION,
} from '../../common/support/accessControlConstants';
import { hasAccess } from '../../common/support/utils/accessControlHelpers';
import { createInitialState } from '../reducers/AccessControlReducer';
import { LOADING_STATUS as LoadingStatus } from '../support/constants';

/**
 * Return AccessControl reducer state from global redux state.
 */
export function getState(state: RootState): AccessControlReducer {
  return (state && state.accessControl) || createInitialState();
}

/**
 * Get loading status of Access Control
 */
export const getLoadingStatus: (state: RootState) => number = createSelector(getState, (state) => state.loadingStatus);
export const isAccessControlLoading: (state: RootState) => boolean = createSelector(
  getState,
  (state) => state.loadingStatus < 2
);
export const getError: (state: RootState) => Record<string, any> | null | undefined = createSelector(
  getState,
  (state) => state.error
);

/**
 * Get all loaded roles for current user.
 */
export const getRoles: (state: RootState) => AccessResourcePermissions | undefined = createSelector(
  getState,
  (state) => state.data
);

export const selectAppDefaultRoles: (state: RootState) => AccessResourcePermissions | null | undefined = createSelector(
  getState,
  (state) => state.appDefaultRoles
);
export const selectAppDefaultRolesList: (state: RootState) => { id: string; name: AccessRoleResource }[] = createSelector(
  selectAppDefaultRoles,
  (appDefaultRoles) =>
    Object.entries(appDefaultRoles || {}).map(([id, name]) => ({
      id,
      name,
    }))
);
export const getAdNetworkPermission: (state: RootState) => number | null | undefined = createSelector(
  getRoles,
  (state) => state?.ad_networks.permission
);
export const isAuthorized: (
  state: RootState,
  access?: {
    permission: PermissionLevel;
    accessResources: AccessResource[];
  }
) => boolean | null | undefined = createSelector(
  getRoles,
  (_, access) => access,
  (roles, access) => {
    return access && hasAccess(access.accessResources, access.permission, roles);
  }
);
export const isLoading: (state: RootState) => Boolean = createSelector(getState, (state) => {
  if (!state.loadingStatus) {
    return true;
  }

  return Boolean(state.loadingStatus === LoadingStatus.LOADING);
});
export const selectHasAdNetworkOrAgencyTaggedDataRestrictions: (state: RootState) => boolean = createSelector(
  getRoles,
  (roles) => {
    return !hasAccess([AD_NETWORKS, AGENCY_TAGGED_DATA], READ_PERMISSION, roles);
  }
);
export const isRevenueDataRestricted: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return !hasAccess([REVENUE_DATA], READ_PERMISSION, roles);
});
export const hasAppLevelSettingsWriteAccess: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([APP_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const hasChannelLevelSettingsWriteAccess: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([CHANNEL_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const getHasLinkLevelWriteAccess: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([LINK_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const getHasTemplateOnlyAccess: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return (roles?.link_level_settings?.access_filter || []).includes(ACCESS_FEATURE.TEMPLATE_ONLY);
});
export const selectHasAgencyTaggedDataRestriction: (state: RootState) => boolean = createSelector(getRoles, (roles) => {
  return !hasAccess([AGENCY_TAGGED_DATA], READ_PERMISSION, roles);
});
